import React from 'react';
import Api from 'api/Api';
import useNotificationStore, {
    NotificationTypes,
} from 'store/useNotificationStore';
import type {
    Report,
    AssignmentInfo,
    SubmitReportPayload,
} from 'api/report/types';
import { isTimesheetReport } from 'utils/report';
import useMeStore from 'store/useMeStore';

export default function useReport(assignmentID: string) {
    const token = useMeStore(state => state.token);
    const [report, setReport] = React.useState<Report | null>(null);
    const [assignmentInfo, setAssignmentInfo] =
        React.useState<AssignmentInfo | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [error, setError] = React.useState(false);
    const pushNotification = useNotificationStore(state => state.push);

    React.useEffect(() => {
        fetch();
    }, [assignmentID]);

    const fetch = React.useCallback(async () => {
        if (loading) {
            return;
        }

        setLoading(true);
        setError(false);
        try {
            const response = await Api.assignment.fetchAssignmentReport(
                token,
                assignmentID
            );

            const { status, data } = response;
            if (status === 200) {
                setAssignmentInfo(data.assignmentInfo);
                setReport(data.report);
            } else {
                throw new Error('');
            }
        } catch (err) {
            console.log(err);
            setError(true);
            pushNotification(
                NotificationTypes.error,
                'Det gick inte att hämta tidrapport.',
                'assignment',
                2
            );
        }
        setLoading(false);
    }, [assignmentID, token]);

    const submit = async (payload: SubmitReportPayload) => {
        if ((submitting && !success) || !report) {
            return;
        }
        setSuccess(false);
        setSubmitting(true);
        try {
            const response = await Api.assignment.submitAssignmentReport(
                token,
                assignmentID,
                payload
            );
            const { status } = response;
            if (status === 200) {
                setSuccess(true);
                fetch();
                pushNotification(
                    NotificationTypes.success,
                    'Tidrapporten har skickats in.',
                    'assignment',
                    1
                );
            } else {
                throw new Error('');
            }
        } catch (err: any) {
            console.log(err);
            pushNotification(
                NotificationTypes.error,
                err?.response?.data?.message ||
                    'Det gick inte att skicka in tidrapporten, försök igen.',
                'assignment',
                2
            );
        }
        setSubmitting(false);
    };

    return {
        submit,
        report,
        totalReportedDays:
            report && isTimesheetReport(report)
                ? report.data.totalReportedDays
                : undefined,
        assignmentInfo,
        loading,
        submitting,
        success,
        error,
    };
}
